/**
 * Tras crear o actualizar un pedido con pagos parciales / plan separe,
 * genera un link Bold por cada fila Bold sin `id_transaccion` y persiste con PATCH /list/:id.
 */
import axiosInstance from '../../api/axiosInstance'
import { createBoldPaymentLink, getBoldLinkDetails } from './boldService'
import {
  attemptTwice,
  BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED,
  readBoldPatchSaveError
} from './boldPatchRetry'

export interface BoldPartialLinkItem {
  url: string
  label: string
  index: number
}

export interface BoldPartialPatchContext {
  status: string
  paymentMethod: string
  oldDataOrder: {
    status: string
    customer_note: string
    line_items: unknown[]
    transaction_id: string
  }
  pagos: Array<Record<string, unknown>>
  extraDetails: Record<string, unknown>
}

export type BoldPartialSaveFailureStep = 'bold_link' | 'bold_patch'

const BOLD_LINK_DEBOUNCE_MS = 2500

export function extractPagosFromOrderDetail(detail: Record<string, unknown>): Record<string, unknown>[] {
  const ed = detail?.extraDetails as Record<string, unknown> | undefined
  const fromEd = ed?.pagos
  if (Array.isArray(fromEd)) return fromEd as Record<string, unknown>[]
  const root = detail?.pagos
  if (Array.isArray(root)) return root as Record<string, unknown>[]
  return []
}

export function indicesPagosParcialesBoldSinTx(
  pagos: Array<Record<string, unknown>>
): number[] {
  const out: number[] = []
  pagos.forEach((p, i) => {
    const m = String(p.metodo_pago ?? '').trim().toLowerCase()
    if (m !== 'bold') return
    const tx = String(p.id_transaccion ?? '').trim()
    if (tx) return
    out.push(i)
  })
  return out
}

export function getPagosFromSubmitPayload(data: Record<string, unknown>): Array<Record<string, unknown>> {
  const root = data.pagos
  if (Array.isArray(root)) return root as Record<string, unknown>[]
  const ed = data.extraDetails as Record<string, unknown> | undefined
  const ep = ed?.pagos
  if (Array.isArray(ep)) return ep as Record<string, unknown>[]
  return []
}

/** Arma el array `pagos` para PATCH /list/:id (part / plan_separe). */
export function buildPagosPatchPayload(
  pagos: Array<Record<string, unknown>>,
  paymentMethod: string
): Array<Record<string, unknown>> {
  const pmNorm = paymentMethod.trim().toLowerCase()
  if (pmNorm === 'plan_separe') {
    return pagos.map((p) => {
      const { pago_verificado: _pv, ...rest } = p as Record<string, unknown> & {
        pago_verificado?: unknown
      }
      return rest
    })
  }
  return pagos
}

export function submitPayloadNeedsPartialBoldLinks(data: Record<string, unknown>): boolean {
  const pm = String(
    data.payment_method ?? (data.extraDetails as Record<string, unknown> | undefined)?.payment_method ?? ''
  )
    .trim()
    .toLowerCase()
  if (pm !== 'part' && pm !== 'plan_separe') return false
  return indicesPagosParcialesBoldSinTx(getPagosFromSubmitPayload(data)).length > 0
}

/** Un solo PATCH de los id_transaccion ya obtenidos. Lo usa el reintento manual. */
export async function retrySaveBoldPartialLinksOnOrder(
  orderId: number,
  ctx: BoldPartialPatchContext
): Promise<{ ok: boolean; error?: string }> {
  const token = localStorage.getItem('accessToken')
  if (!token) {
    return { ok: false, error: 'No hay sesión' }
  }

  try {
    await axiosInstance.patch(
      `/list/${orderId}`,
      {
        status: ctx.status,
        payment_method: ctx.paymentMethod,
        old_data_order: ctx.oldDataOrder,
        pagos: ctx.pagos,
        extraDetails: ctx.extraDetails
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    )
    return { ok: true }
  } catch (error: unknown) {
    return {
      ok: false,
      error: readBoldPatchSaveError(error, BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED)
    }
  }
}

/** Un intento de PATCH y, si falla, un segundo intento con los mismos ids. */
export async function saveBoldPartialPatchWithRetry(
  orderId: number,
  ctx: BoldPartialPatchContext,
  onBeforeRetry?: () => void
): Promise<{ ok: boolean; error?: string }> {
  return attemptTwice(
    () => retrySaveBoldPartialLinksOnOrder(orderId, ctx),
    onBeforeRetry
  )
}

export async function generateAndPatchBoldLinksForPartialRows(params: {
  orderId: number
  /** Respuesta de GET /list/:id o del PUT tras guardar (debe incluir `pagos` o extraDetails.pagos) */
  orderDetail?: Record<string, unknown>
  onProgress?: (message: string) => void
}): Promise<{
  ok: boolean
  links: BoldPartialLinkItem[]
  error?: string
  failureStep?: BoldPartialSaveFailureStep
  patchRetryContext?: BoldPartialPatchContext
}> {
  const { orderId, orderDetail: prefetched } = params
  const token = localStorage.getItem('accessToken')
  if (!token) {
    return { ok: false, links: [], error: 'No hay sesión' }
  }

  let detail = prefetched
  if (!detail) {
    const res = await axiosInstance.get(`/list/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    detail = res.data as Record<string, unknown>
  }

  const pagosRaw = extractPagosFromOrderDetail(detail)
  if (pagosRaw.length === 0) {
    return { ok: true, links: [] }
  }

  const pagos = JSON.parse(JSON.stringify(pagosRaw)) as Array<Record<string, unknown>>
  const indices = indicesPagosParcialesBoldSinTx(pagos)
  if (indices.length === 0) {
    return { ok: true, links: [] }
  }

  const storeName = String(
    detail.store_name ?? (detail.extraDetails as Record<string, unknown> | undefined)?.store_name ?? ''
  ).trim()
  const status = String(detail.status ?? 'processing')
  const paymentMethod = String(
    detail.payment_method ??
      (detail.extraDetails as Record<string, unknown> | undefined)?.payment_method ??
      'part'
  )

  const links: BoldPartialLinkItem[] = []
  const total = indices.length

  for (let n = 0; n < indices.length; n++) {
    const i = indices[n]
    params.onProgress?.(`Generando link Bold ${n + 1} de ${total}…`)
    const row = pagos[i]
    const totalAmount = Math.round(Number(row.valor) || 0)
    if (totalAmount <= 0) {
      return {
        ok: false,
        links,
        error: `El pago parcial ${i + 1} (Bold) tiene valor inválido o cero`,
        failureStep: 'bold_link'
      }
    }
    const reference = `ped~${orderId}~par~${i}`
    const description = `Pago parcial ${i + 1} — pedido ${orderId}`

    const linkResult = await createBoldPaymentLink({
      totalAmount,
      reference,
      description,
      store_name: storeName
    })

    if (!linkResult.success || !linkResult.payment_link) {
      return {
        ok: false,
        links,
        error: linkResult.error || `No se pudo crear el link Bold (fila ${i + 1})`,
        failureStep: 'bold_link'
      }
    }

    params.onProgress?.(`Consultando detalle del link ${n + 1} de ${total}…`)
    await new Promise((r) => setTimeout(r, BOLD_LINK_DEBOUNCE_MS))

    const linkDetails = await getBoldLinkDetails(linkResult.payment_link, storeName)
    const transactionIdToSave =
      linkDetails.success && linkDetails.transaction_id
        ? linkDetails.transaction_id
        : linkResult.payment_link

    row.id_transaccion = transactionIdToSave
    const url = (linkResult.url || linkResult.payment_link) as string
    links.push({ url, label: `Pago parcial ${i + 1} (Bold)`, index: i })
  }

  const extraDetails = {
    ...((detail.extraDetails as Record<string, unknown>) || {})
  }
  delete extraDetails.pagos

  const lineItems = (detail.lineItems as unknown[]) || (detail.line_items as unknown[]) || []

  const old_data_order = {
    status: String(detail.status ?? ''),
    customer_note: String(extraDetails.customer_note ?? ''),
    line_items: lineItems,
    transaction_id: String(extraDetails.transaction_id ?? '').trim()
  }

  const pagosPayload = buildPagosPatchPayload(pagos, paymentMethod)
  const patchContext: BoldPartialPatchContext = {
    status,
    paymentMethod,
    oldDataOrder: old_data_order,
    pagos: pagosPayload,
    extraDetails
  }

  params.onProgress?.('Finalizando…')

  const saveResult = await saveBoldPartialPatchWithRetry(orderId, patchContext, () => {
    params.onProgress?.('Reintentando guardado de los enlaces…')
  })

  if (!saveResult.ok) {
    return {
      ok: false,
      links,
      error: saveResult.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED,
      failureStep: 'bold_patch',
      patchRetryContext: patchContext
    }
  }

  return { ok: true, links }
}
